import React from 'react';

import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {UsersByIds, UsersByIdsVariables} from 'nav/presence/__generated/UsersByIds';
import {USERS_BY_IDS_QUERY} from 'nav/presence/ResourcePresenceView';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import EntityStore from 'stores/EntityStore';
import {PublishableVersionStatus} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {typeToName} from 'util/NameUtil';

export const DocumentBanner = {
  Archived,
  ReadOnly,
  PreviousVersion,
  ReviewLocked,
  ExternalChangeLocked,
  Publishing,
  ResourceLocked,
  ReadOnlyMode,
};

function Archived({docType}: {docType: string}): JSX.Element {
  return <span>Editing disabled on archived {typeToName(docType)}.</span>;
}

function ReadOnly({docType}: {docType: string}): JSX.Element {
  return <span>Editing disabled on read-only {typeToName(docType)}.</span>;
}

function PreviousVersion({
  docType,
  isEvaluationPersisted,
  onNavigateToLatest,
}: {
  docType: string;
  isEvaluationPersisted: boolean;
  onNavigateToLatest?: () => void;
}): JSX.Element {
  return (
    <span>
      Editing is disabled because you're viewing a previous {typeToName(docType)} version.
      {onNavigateToLatest && (
        <>
          {' '}
          <a onClick={onNavigateToLatest}>View the latest version.</a>
        </>
      )}
      {!isEvaluationPersisted && (
        <>
          {' '}
          <>
            Formulas have been re-computed and may differ from original values in rare cases.{' '}
            <a
              onClick={() => {
                window.open(
                  'https://help.benchling.com/hc/en-us/articles/33496079947405-Table-formula-updates',
                  '_blank'
                );
              }}
            >
              Learn more.
            </a>
          </>
        </>
      )}
    </span>
  );
}

function ReviewLocked({
  status,
  docType,
  isLatest,
  onNavigateToLatest,
}: {
  status: ReviewStatus;
  docType: string;
  isLatest?: boolean;
  onNavigateToLatest?: () => void;
}): JSX.Element {
  return (
    <span>
      {ReviewUtil.getEditWarning(status, docType)}
      {isLatest === false && docType && onNavigateToLatest && (
        <>
          {' '}
          You're viewing a previous {typeToName(docType)} version.{' '}
          <a onClick={onNavigateToLatest}>View the latest version.</a>
        </>
      )}
    </span>
  );
}

function ExternalChangeLocked({isUnsaved}: {isUnsaved: boolean}): JSX.Element {
  return (
    <span>
      Editing disabled on previous versions.{' '}
      {isUnsaved ? 'Copy unsaved changes before refreshing.' : 'Refresh to edit latest version.'}
    </span>
  );
}

function Publishing({
  publishingStatus,
  onCreateDraft,
}: {
  publishingStatus: PublishableVersionStatus;
  onCreateDraft: () => void;
}): JSX.Element {
  return (
    <span>
      Editing is disabled on {publishingStatus.toLowerCase()} versions. You can{' '}
      <a onClick={onCreateDraft} {...getTestProps({component: 'DocumentBanner', element: 'createDraftLink'})}>
        create new draft
      </a>{' '}
      to make changes.
    </span>
  );
}

function ResourceLocked({userId, docType}: {userId: string; docType: string}): JSX.Element {
  const userIds = [userId];
  const result = useBenchlingQuery<UsersByIds, UsersByIdsVariables>(USERS_BY_IDS_QUERY, {
    variables: {userIds},
  });
  const defaultMessage = (
    <>Editing is disabled because another user is working on this {typeToName(docType)}</>
  );

  if (result.type === QueryResultType.ERROR || result.type === QueryResultType.PENDING) {
    return defaultMessage;
  }

  const user = result.data.users[0];
  if (isInaccessible(user)) {
    return defaultMessage;
  }

  return user.id === EntityStore.currentUser()?.id ? (
    <>Editing is disabled because you are editing this {typeToName(docType)} in another window</>
  ) : (
    <>
      Editing is disabled because <b>{user.name}</b> is working on this {typeToName(docType)}
    </>
  );
}

function ReadOnlyMode({onExitReadOnlyMode}: {onExitReadOnlyMode: () => void}): JSX.Element {
  return (
    <span>
      Viewing in read-only mode.{' '}
      <a
        onClick={onExitReadOnlyMode}
        {...getTestProps({component: 'DocumentBanner', element: 'exitReadOnlyModeLink'})}
      >
        Exit read-only mode
      </a>
    </span>
  );
}
